import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import { CallRoute, TrackingNumberDetail, TrackingNumberStatus } from '../../../interfaces/calls';
import voiceService from '../../../services/appointments/calls';
import queuesService from '../../../services/appointments/queues';

interface TrackingNumberFormModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  trackingNumberToEdit?: TrackingNumberDetail | null;
}

export const TrackingNumberFormModal = ({
  open,
  onClose,
  onSuccess,
  trackingNumberToEdit,
}: TrackingNumberFormModalProps) => {
  const [loading, setLoading] = useState(false);
  const notify = useNotify();

  // Data Sources
  const [routes, setRoutes] = useState<CallRoute[]>([]);

  // Form State
  const [trackingNumber, setTrackingNumber] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [label, setLabel] = useState('');
  const [status, setStatus] = useState<TrackingNumberStatus>(TrackingNumberStatus.ACTIVE);
  const [isMainLine, setIsMainLine] = useState(false);
  const [isTollFreeLine, setIsTollFreeLine] = useState(false);
  const [callRouteId, setCallRouteId] = useState<number | null>(null);
  
  // Capabilities
  const [enabledForVoiceCalls, setEnabledForVoiceCalls] = useState(false);
  const [enabledForInboundCalls, setEnabledForInboundCalls] = useState(false);
  const [enabledForOutboundCalls, setEnabledForOutboundCalls] = useState(false);
  const [enabledForVoicemail, setEnabledForVoicemail] = useState(false);
  const [enabledForReminders, setEnabledForReminders] = useState(false);

  useEffect(() => {
    if (open) {
        fetchRoutes();
        if (trackingNumberToEdit) {
            setTrackingNumber(trackingNumberToEdit.trackingNumber);
            setIdentifier(''); // Identifier usually cannot be changed or is not exposed in Detail DTO, check interface
            setLabel(trackingNumberToEdit.label || '');
            setStatus(trackingNumberToEdit.status);
            setIsMainLine(trackingNumberToEdit.isMainLine);
            setIsTollFreeLine(trackingNumberToEdit.isTollFreeLine);
            setCallRouteId(trackingNumberToEdit.callRouteId);
            setEnabledForVoiceCalls(trackingNumberToEdit.enabledForVoiceCalls);
            setEnabledForInboundCalls(trackingNumberToEdit.enabledForInboundCalls);
            setEnabledForOutboundCalls(trackingNumberToEdit.enabledForOutboundCalls);
            setEnabledForVoicemail(trackingNumberToEdit.enabledForVoicemail);
            setEnabledForReminders(trackingNumberToEdit.enabledForReminders);
        } else {
            resetForm();
        }
    }
  }, [open, trackingNumberToEdit]);

  const fetchRoutes = async () => {
    try {
        const response = await queuesService.getAllRoutes();
        if (response.success && response.data) {
            setRoutes(response.data);
        }
    } catch (error) {
        console.error('Failed to fetch routes', error);
    }
  };

  const handleSubmit = async () => {
    if (!trackingNumber) {
        notify('Tracking Number is required', { type: 'warning' });
        return;
    }

    // Identifier is required for creation
    if (!trackingNumberToEdit && !identifier) {
        notify('Identifier is required', { type: 'warning' });
        return;
    }

    try {
      setLoading(true);
      
      if (trackingNumberToEdit) {
        await voiceService.updateTrackingNumber(trackingNumberToEdit.id, {
            status,
            label,
            callRouteId: callRouteId || undefined,
            enabledForVoiceCalls,
            enabledForInboundCalls,
            enabledForOutboundCalls,
            enabledForVoicemail,
            enabledForReminders
        });
        notify('Tracking number updated successfully', { type: 'success' });
      } else {
        await voiceService.createTrackingNumber({
            trackingNumber,
            identifier,
            status,
            label,
            isMainLine,
            isTollFreeLine,
            callRouteId: callRouteId || undefined,
            enabledForVoiceCalls,
            enabledForInboundCalls,
            enabledForOutboundCalls,
            enabledForVoicemail,
            enabledForReminders
        });
        notify('Tracking number created successfully', { type: 'success' });
      }

      onSuccess();
      onClose();
      if (!trackingNumberToEdit) resetForm();
    } catch (error) {
      notify(`Failed to ${trackingNumberToEdit ? 'update' : 'create'} tracking number`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setTrackingNumber('');
    setIdentifier('');
    setLabel('');
    setStatus(TrackingNumberStatus.ACTIVE);
    setIsMainLine(false);
    setIsTollFreeLine(false);
    setCallRouteId(null);
    setEnabledForVoiceCalls(false);
    setEnabledForInboundCalls(false);
    setEnabledForOutboundCalls(false);
    setEnabledForVoicemail(false);
    setEnabledForReminders(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>{trackingNumberToEdit ? 'Edit Tracking Number' : 'Create Tracking Number'}</DialogTitle>
      <DialogContent>
        <Box mt={2} display="flex" flexDirection="column" gap={3}>
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <TextField 
                        fullWidth 
                        label="Tracking Number" 
                        value={trackingNumber} 
                        onChange={(e) => setTrackingNumber(e.target.value)} 
                        placeholder="+1234567890"
                        helperText="Must include country code (e.g. +1)"
                        disabled={!!trackingNumberToEdit} // Usually tracking number itself is not editable
                    />
                </Grid>
                {!trackingNumberToEdit && (
                    <Grid item xs={6}>
                        <TextField 
                            fullWidth 
                            label="Identifier" 
                            value={identifier} 
                            onChange={(e) => setIdentifier(e.target.value)} 
                            helperText="Unique identifier for internal use"
                        />
                    </Grid>
                )}
                <Grid item xs={6}>
                    <FormControl fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select value={status} label="Status" onChange={(e) => setStatus(e.target.value as TrackingNumberStatus)}>
                            <MenuItem value={TrackingNumberStatus.ACTIVE}>Active</MenuItem>
                            <MenuItem value={TrackingNumberStatus.INACTIVE}>Inactive</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12}>
                    <TextField 
                        fullWidth 
                        label="Label" 
                        value={label} 
                        onChange={(e) => setLabel(e.target.value)} 
                    />
                </Grid>
                <Grid item xs={12}>
                    <FormControl fullWidth>
                        <InputLabel>Call Route</InputLabel>
                        <Select 
                            value={callRouteId || ''} 
                            label="Call Route" 
                            onChange={(e) => setCallRouteId(e.target.value ? Number(e.target.value) : null)}
                        >
                            <MenuItem value="">
                                <em>None</em>
                            </MenuItem>
                            {routes.map((route) => (
                                <MenuItem key={route.id} value={route.id}>{route.displayName}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>

            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <FormControlLabel
                        control={<Switch checked={isMainLine} onChange={(e) => setIsMainLine(e.target.checked)} />}
                        label="Is Main Line"
                        disabled={!!trackingNumberToEdit} // Prevent changing main line status on edit if restricted
                    />
                </Grid>
                <Grid item xs={6}>
                    <FormControlLabel
                        control={<Switch checked={isTollFreeLine} onChange={(e) => setIsTollFreeLine(e.target.checked)} />}
                        label="Is Toll Free"
                        disabled={!!trackingNumberToEdit}
                    />
                </Grid>
            </Grid>

            <Typography variant="subtitle2" mt={1}>Capabilities</Typography>
            <Grid container spacing={2}>
                <Grid item xs={4}>
                    <FormControlLabel
                        control={<Switch checked={enabledForVoiceCalls} onChange={(e) => setEnabledForVoiceCalls(e.target.checked)} />}
                        label="Voice Calls"
                    />
                </Grid>
                <Grid item xs={4}>
                    <FormControlLabel
                        control={<Switch checked={enabledForInboundCalls} onChange={(e) => setEnabledForInboundCalls(e.target.checked)} />}
                        label="Inbound Calls"
                    />
                </Grid>
                <Grid item xs={4}>
                    <FormControlLabel
                        control={<Switch checked={enabledForOutboundCalls} onChange={(e) => setEnabledForOutboundCalls(e.target.checked)} />}
                        label="Outbound Calls"
                    />
                </Grid>
                <Grid item xs={4}>
                    <FormControlLabel
                        control={<Switch checked={enabledForVoicemail} onChange={(e) => setEnabledForVoicemail(e.target.checked)} />}
                        label="Voicemail"
                    />
                </Grid>
                <Grid item xs={4}>
                    <FormControlLabel
                        control={<Switch checked={enabledForReminders} onChange={(e) => setEnabledForReminders(e.target.checked)} />}
                        label="Reminders"
                    />
                </Grid>
            </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading || (!trackingNumberToEdit && (!trackingNumber || !identifier))}>
          {trackingNumberToEdit ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
